import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { useGlobalStore } from '@stores/globalStore';

import services from './data';
import { DutyResolver } from './DutyResolver';
import {
  applyMapping,
  autoDetectMapping,
  currency,
  normalize,
  readSheet,
  readWorkbook,
  SLIP_FIELDS,
} from './helpers';
import { ImportSummary } from './ImportSummary';
import { MappingPanel } from './MappingPanel';

const SUMMARY_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'staff', label: 'Staff' },
  { key: 'customer', label: 'Customer' },
  { key: 'amount', label: 'Amount' },
];

const dutyLabel = (duty) =>
  `${duty.first_name} ${duty.last_name}${duty.shift_name ? ` - ${duty.shift_name}` : ''}`;

const buildDutyOptions = (duties) =>
  duties.map((duty) => ({
    value: duty.cashier_duty_id,
    label: dutyLabel(duty),
    first_name: duty.first_name,
    last_name: duty.last_name,
  }));

const CreditSlipImport = () => {
  const creditCustomers = useGlobalStore((state) => state.creditCustomers);
  const taxList = useGlobalStore((state) => state.taxList);

  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [defaults, setDefaults] = useState({});
  const [dutyOptionsByDate, setDutyOptionsByDate] = useState({});
  const [duplicateStaffByDate, setDuplicateStaffByDate] = useState({});
  const [resolutions, setResolutions] = useState({});
  const [loadingDuties, setLoadingDuties] = useState(false);
  const [productCache, setProductCache] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState([]);

  const parsedRows = useMemo(
    () => rawRows.map((row) => applyMapping(row, SLIP_FIELDS, mapping, defaults)),
    [rawRows, mapping, defaults],
  );

  const customerIndex = useMemo(() => {
    const byMobile = new Map();
    const byName = new Map();
    (creditCustomers || []).forEach((customer) => {
      const mobile = customer.basic_details?.mobile;
      const name = normalize(
        `${customer.basic_details?.first_name || ''} ${
          customer.basic_details?.last_name || ''
        }`,
      );
      if (mobile) byMobile.set(mobile, customer);
      if (name) {
        if (byName.has(name)) {
          byName.set(name, null); // ambiguous
        } else {
          byName.set(name, customer);
        }
      }
    });
    return { byMobile, byName };
  }, [creditCustomers]);

  const uniqueDates = useMemo(
    () => [...new Set(parsedRows.map((row) => row.date).filter(Boolean))],
    [parsedRows],
  );

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResults([]);
    setResolutions({});
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const workbook = readWorkbook(event.target.result);
      const { headers: sheetHeaders, rows } = readSheet(
        workbook,
        workbook.SheetNames[0],
      );
      setHeaders(sheetHeaders);
      setRawRows(rows);
      setMapping(autoDetectMapping(sheetHeaders, SLIP_FIELDS));
      setDefaults({});
    };
    reader.readAsArrayBuffer(file);
  };

  const handleMappingChange = (key, value) => {
    setMapping((prev) => ({ ...prev, [key]: value }));
  };

  const handleDefaultChange = (key, value) => {
    setDefaults((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (uniqueDates.length === 0) return;

    const missingDates = uniqueDates.filter(
      (date) => !dutyOptionsByDate[date],
    );
    if (missingDates.length === 0) return;

    let cancelled = false;
    setLoadingDuties(true);

    Promise.all(
      missingDates.map(async (date) => {
        try {
          const [openRes, pastRes] = await Promise.all([
            services.getOpenDutyList(date),
            services.getPastDutyList(date),
          ]);
          const duties = [
            ...(openRes.data?.data?.duties_list || []),
            ...(pastRes.data?.data?.duties_list || []),
          ];
          return { date, duties };
        } catch (error) {
          console.log(error);
          return { date, duties: [] };
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setDutyOptionsByDate((prev) => {
        const next = { ...prev };
        entries.forEach(({ date, duties }) => {
          next[date] = buildDutyOptions(duties);
        });
        return next;
      });
      setDuplicateStaffByDate((prev) => {
        const next = { ...prev };
        entries.forEach(({ date, duties }) => {
          const nameCount = {};
          duties.forEach((duty) => {
            const key = normalize(`${duty.first_name} ${duty.last_name}`);
            nameCount[key] = (nameCount[key] || 0) + 1;
          });
          next[date] = nameCount;
        });
        return next;
      });
      setLoadingDuties(false);
    });

    return () => {
      cancelled = true;
    };
  }, [uniqueDates, dutyOptionsByDate]);

  const autoMatchDuty = (date, staffText) => {
    const options = dutyOptionsByDate[date];
    if (!options) return null;
    const target = normalize(staffText);
    const dupCount = duplicateStaffByDate[date]?.[target] || 0;
    if (dupCount !== 1) return null;
    const match = options.find(
      (option) => normalize(`${option.first_name} ${option.last_name}`) === target,
    );
    return match ? match.value : null;
  };

  const unresolvedPairs = useMemo(() => {
    const map = new Map();
    parsedRows.forEach((row) => {
      if (!row.date || !row.staff) return;
      const key = `${row.date}||${row.staff}`;
      if (autoMatchDuty(row.date, row.staff)) return;
      if (resolutions[key]) return;
      if (!map.has(key)) {
        map.set(key, { key, date: row.date, staff: row.staff, count: 0 });
      }
      map.get(key).count += 1;
    });
    return [...map.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedRows, dutyOptionsByDate, duplicateStaffByDate, resolutions]);

  const handleResolve = (key, cashierDutyId) => {
    setResolutions((prev) => ({ ...prev, [key]: cashierDutyId }));
  };

  const getProductList = async (date) => {
    if (productCache[date]) return productCache[date];
    try {
      const response = await services.getProductList(date);
      const list = (response.data?.data || []).map((item) => ({
        uuid: item.uuid,
        name: normalize(item.product || item.name),
        price: item.price.map((p) => ({
          value: p.uuid || p.price,
          label: p.price,
        })),
        product_type: item.hasOwnProperty('number_of_units')
          ? 'Lubes'
          : 'Fuel',
      }));
      setProductCache((prev) => ({ ...prev, [date]: list }));
      return list;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const resolveCustomer = (row) => {
    if (row.customer_mobile && customerIndex.byMobile.has(row.customer_mobile)) {
      return customerIndex.byMobile.get(row.customer_mobile);
    }
    const match = customerIndex.byName.get(normalize(row.customer));
    return match || null;
  };

  const resolveTax = (name) => {
    const match = (taxList || []).find(
      (item) => normalize(item.label) === normalize(name),
    );
    if (!match) return null;
    return {
      label: match.label,
      multiplier: match.multiplier || 0,
      value: (match.value || []).map((item) => item.uuid),
    };
  };

  const buildSlipItem = (row, products) => {
    const item = {
      slip_number: row.slip_number || '',
      credit_customer_id: row.__customerId,
      vehicle_id: row.__vehicleId || undefined,
      amount: 0,
      discount: currency(row.discount || 0),
      discount_type: 'amount',
      tax: [],
      taxName: '',
      product_type: 'CashGiven',
      product_id: null,
      fuel_price_id: null,
      amount_given: 0,
      quantity: 0,
      notes: row.notes || '',
    };

    const product = row.product
      ? products.find((p) => p.name === normalize(row.product))
      : null;

    let taxMultiplier = 0;

    if (product) {
      const priceMatch =
        product.price.find((p) => Number(p.label) === Number(row.price)) ||
        product.price[0];
      const priceValue = Number(priceMatch?.label || 0);

      item.product_id = product.uuid;
      item.product_type = product.product_type;
      item.fuel_price_id = priceMatch?.value || null;
      item.quantity = row.quantity || (priceValue ? row.amount / priceValue : 0);
      item.amount = currency(row.amount || 0);
      item.amount_given = currency(row.cash_given || 0);

      if (product.product_type !== 'Fuel' && row.tax) {
        const taxMatch = resolveTax(row.tax);
        if (taxMatch) {
          item.tax = taxMatch.value;
          item.taxName = taxMatch.label;
          taxMultiplier = taxMatch.multiplier;
        }
      }
    } else {
      item.amount = 0;
      item.amount_given = currency(row.amount || 0);
    }

    const amount = parseFloat(item.amount) || 0;
    const cashGiven = parseFloat(item.amount_given) || 0;
    const tax = amount * (taxMultiplier / 100);
    item.totalAmount = (amount + tax + cashGiven).toFixed(2);

    return item;
  };

  const submitImport = async () => {
    setIsSubmitting(true);

    const rowResults = [];
    const groups = new Map();

    for (let index = 0; index < parsedRows.length; index += 1) {
      const row = parsedRows[index];
      const rowNumber = index + 2;

      if (!row.date || !row.staff || !row.customer || !row.amount) {
        rowResults.push({
          row: rowNumber,
          data: row,
          status: 'failed',
          message: 'Missing required field(s): date, staff, customer or amount',
        });
        continue;
      }

      const cashierDutyId =
        autoMatchDuty(row.date, row.staff) ||
        resolutions[`${row.date}||${row.staff}`];

      if (!cashierDutyId) {
        rowResults.push({
          row: rowNumber,
          data: row,
          status: 'skipped',
          message: `No duty found for ${row.staff} on ${row.date}`,
        });
        continue;
      }

      const customer = resolveCustomer(row);
      if (!customer) {
        rowResults.push({
          row: rowNumber,
          data: row,
          status: 'skipped',
          message: `Credit customer not found: ${row.customer}`,
        });
        continue;
      }

      row.__customerId = customer.uuid;

      if (row.vehicle_number) {
        try {
          const vehicleRes = await services.getCustomerVehicles(customer.uuid);
          const vehicle = (vehicleRes.data?.data || []).find(
            (v) => normalize(v.vehicle_no) === normalize(row.vehicle_number),
          );
          if (vehicle) {
            row.__vehicleId = vehicle.uuid;
          }
        } catch (error) {
          console.log(error);
        }
      }

      const products = await getProductList(row.date);
      const item = buildSlipItem(row, products);

      const groupKey = `${row.date}||${cashierDutyId}`;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          time_stamp: row.date,
          cashier_duty_id: cashierDutyId,
          items: [],
        });
      }
      groups.get(groupKey).items.push({ rowNumber, row, item });
    }

    for (const group of groups.values()) {
      try {
        await services.createCreditSlips({
          credit_slips_list: group.items.map((entry) => entry.item),
          cashier_duty_id: group.cashier_duty_id,
          time_stamp: group.time_stamp,
        });
        group.items.forEach((entry) => {
          rowResults.push({
            row: entry.rowNumber,
            data: entry.row,
            status: 'added',
          });
        });
      } catch (error) {
        const message =
          error.response?.data?.message || 'Failed to create credit slip(s)';
        group.items.forEach((entry) => {
          rowResults.push({
            row: entry.rowNumber,
            data: entry.row,
            status: 'failed',
            message,
          });
        });
      }
    }

    rowResults.sort((a, b) => a.row - b.row);
    setResults(rowResults);
    setIsSubmitting(false);

    const failedOrSkipped = rowResults.filter(
      (item) => item.status !== 'added',
    ).length;
    if (failedOrSkipped === 0) {
      toast.success('Credit slips imported successfully');
      setRawRows([]);
      setHeaders([]);
      setFileName('');
    } else {
      toast.success(
        'Import finished — some rows need attention, see summary below.',
      );
    }
  };

  const canImport =
    headers.length > 0 && !loadingDuties && unresolvedPairs.every((p) => resolutions[p.key]);

  return (
    <>
      <section className="widget full-width form-table">
        <header>
          <h3>Import Credit Slips</h3>
          <div className="actions">
            <div className="input-wrapper file-upload">
              <input
                type="file"
                id="credit-slip-file"
                accept=".xlsx, .xls"
                onChange={handleFile}
              />
            </div>
          </div>
        </header>
        {fileName && (
          <main>
            <p className="migration-hint">
              {fileName} — {parsedRows.length} row(s) detected
            </p>
          </main>
        )}
      </section>

      {headers.length > 0 && (
        <>
          <MappingPanel
            fields={SLIP_FIELDS}
            headers={headers}
            mapping={mapping}
            defaults={defaults}
            onChange={handleMappingChange}
            onDefaultChange={handleDefaultChange}
          />

          <DutyResolver
            pairs={unresolvedPairs}
            dutyOptions={dutyOptionsByDate}
            resolutions={resolutions}
            onResolve={handleResolve}
            loading={loadingDuties}
          />

          <div className="form-actions">
            <button
              className={`submit ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting || !canImport}
              onClick={submitImport}
            >
              Import {parsedRows.length} slip(s)
            </button>
            {unresolvedPairs.length > 0 && (
              <span className="migration-hint">
                Resolve all staff mismatches above before importing.
              </span>
            )}
          </div>
        </>
      )}

      {isSubmitting && <DropLoaderLarge />}

      <ImportSummary
        title="Credit slip import summary"
        results={results}
        columns={SUMMARY_COLUMNS}
      />
    </>
  );
};

export { CreditSlipImport };
